import React, { useState, useEffect, useMemo } from 'react';
import { Card, Justify, PagingQuery } from 'tea-component';
import Filter from '@/pages/intelligence/pages/TopCharts/components/CommonFilter';
import SearchRecent from '@/pages/intelligence/components/SearchRecent';
import RetentionTable from '@/pages/intelligence/pages/TopCharts/MobileGames/components/Tables/RetentionTable';
import Visualis from '@/pages/intelligence/pages/TopCharts/MobileGames/components/Visualis/RetentionVisualis';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import ExportTable from '../../../../components/ExportTable';
import Loading from '@/pages/intelligence/components/Loading';
import useRetentionLineCharts from '@/pages/intelligence/pages/TopCharts/MobileGames/hooks/useRetentionLineCharts';
import {
  TableSelectStatusType,
  TableSelectionType,
} from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import useRetentionModel, {
  FetchParamsType,
} from '@/pages/intelligence/pages/TopCharts/MobileGames/hooks/useRetentionModel';
import { SortByItemType } from '@/pages/intelligence/components/TableComponents/components/SortBy';
import CardSlot from '@/pages/intelligence/components/CardSlot';
import { fetchTopChartsRetentionRankList } from '@/pages/intelligence/services/topcharts';
import { getFilterName, getFilterValueStorage } from '@/pages/intelligence/components/Filter/util';
import { useSelector, useIntl, history } from 'umi';
import ArrowdownText from '@/pages/intelligence/components/ArrowdownText';
import {
  commonMobileRetentionDefaultFilterValue,
  handleRetentionFilterParamsToApi,
  stripFetchParamsFilter,
} from '@/pages/intelligence/pages/TopCharts/_config/topchartFilterCfg';

import { FilterTimeType, getDbiFilterParams } from '@intelligence/utils/filterUtils';
import type { TopchartFilterCfgType } from '@intelligence/types/topchart';
import moment from 'moment';
import useMarketFilterApi, {
  MarketSpecailType,
} from '@/pages/intelligence/hooks/useMarketSelector';
import {
  SENSOR_TOWER_TAG_KEYS,
  RETENTION_D_RANGE_KEYS,
  buildAppMagicTagSpecialParams,
} from '@/pages/intelligence/components/Filter/FilterBase/_config';
import useMobileGameEngineOptions from '@intelligence/hooks/useMobileGameEngineOptions';
import { LANGUAGE_CN } from '@/pages/home/_config/config';
import useSenTowerTag from '@/pages/intelligence/hooks/useSenTowerTag';
import useEarliesTimeHasData from '@/pages/intelligence/hooks/useEarliesTimeHasData';
import useHotTagsOptions from '@intelligence/hooks/useHotTagsOptions';
import { useQueryDefinition } from '@intelligence/hooks/useQueryDefinition';
import { parseUrlFilterParams } from '@intelligence/v3/utils/filterTagNameAnalyser';
import { generateGenreSelectValueSimple } from '@intelligence/v3/utils/genre';
import { GENRE_SOURCES_MULTI_DATASOURCE_TOPCHARTS_PAGE } from '@intelligence/components/GenreSelect/constants';
import { AppMagicGenreTagType } from '@intelligence/components/TableComponents/components/AppMagicGenreTagList';
import { hasDataTimerParams } from '@intelligence/utils/timerDefautParams';
import {
  getTopMobilesRetentionCustomValidUrlParams,
  topchartMobileRetentionValidCfg,
  hasShortLinkRatio,
} from '@intelligence/_config/urlPageValidCfg';
import {
  getOriginalShortLinkFilter,
  URL_FILTER_KEY,
  updateShortLinkPath,
  URL_CHART_METRIC,
  URL_SHORT_LINK_NAME,
  URL_CHART_TYPE,
  URL_SORT_NAME,
  URL_ORDER,
  URL_RATIO_TYPE,
} from '@intelligence/utils/urlPathCfg';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';
import { getMobileTopChartsFilterTips } from '@intelligence/pages/TopCharts/MobileGames/_config/mobileTopChartsFilterTips';
import { buildTopChartsTagClickHandler } from '@intelligence/v3/utils/sensorTowerTagColumns';
import useMobileTopChartsTagDetails from '@intelligence/v3/pages/TopCharts/MobileGames/_hooks/useMobileTopChartsTagDetails';

interface IRetentionProps {
  /** TopCharts 主入口显式传递；复用入口不传时沿用 common 作用域。 */
  pageId?: string;
}

export default function Retention(props: IRetentionProps) {
  const { pageId } = props;
  const pageLogInfo = dbiPageLogMes.topChartsMobileGamesRetention;
  const entityType = 'mobile';
  const hasUrlShortLink = hasShortLinkRatio.topMobileRetention;
  const [shortLinkLoading, setShortLinkLoading] = useState(hasUrlShortLink);
  const [urlCustomParams, setUrlCustomParams] = useState({});
  const [shortLinkValidQuery, setShortLinkValidQuery] = useState({
    [URL_CHART_TYPE]: 'line',
    [URL_CHART_METRIC]: 'D2',
  });
  const [shortLinkFilterValue, setShortLinkFilterValue] = useState(null);
  const { filterLoading, regionOptions } = useMarketFilterApi({
    filterType: MarketSpecailType.retention,
  });
  const { latestTimeTypeHasData } = useEarliesTimeHasData({
    filterType: hasDataTimerParams?.mobileRetention?.type,
    rangeTimeType: FilterTimeType.sameMonth,
  });
  const { formatMessage, locale } = useIntl();
  const isCn = locale === LANGUAGE_CN;
  const { tagLoading, tagConfig } = useSenTowerTag({});

  // 热门标签
  const { hotTagsOptions, filterTagName, hotTagsLoading } = useHotTagsOptions('mobile');

  // 品类 options（四源 + App Magic 四级；HotTag 解析品类筛选器参数）
  const {
    loadingStd: definitionsLoading,
    genreMap,
    genreOptionsMap,
    filterTags,
  } = useQueryDefinition();
  const { mobileGameEngineOptions } = useMobileGameEngineOptions();

  const appMagicTagConfig = useMemo(
    () => buildAppMagicTagSpecialParams(filterTags, isCn),
    [isCn, filterTags],
  );

  const initPage = async () => {
    setShortLinkLoading(true);
    // 在url上取token，有则请求接口， 判断异常，返回函数值和url上的其他配置参数
    const { [URL_FILTER_KEY]: urlFilter } = await getOriginalShortLinkFilter();
    if (urlFilter) {
      // 有的筛选器的token时才用后面的参数，没有则不用
      const { params, validQuery } = getTopMobilesRetentionCustomValidUrlParams(
        history,
        topchartMobileRetentionValidCfg,
      );
      setUrlCustomParams({ ...params });
      setShortLinkValidQuery({ ...shortLinkValidQuery, ...validQuery });
      setShortLinkFilterValue(urlFilter);
    }
    setShortLinkLoading(false);
  };

  useEffect(() => {
    buriedPointPage(pageLogInfo);
    // 进入页面时判断是否有tokenQuery等，有则进入
    initPage();
  }, []);

  const defaultFilterValue = {
    ...commonMobileRetentionDefaultFilterValue,
    [filterKeyMap.PLATFORM]: { value: ['appstore', 'googleplay'] },
  };

  const groupedFilter = filterKeyMap.defineFilterGroups(
    [
      {
        key: 'popular',
        titleKey: 'dbi_filter_group_popular',
        filterKeys: [
          filterKeyMap.RANGE_TIME,
          filterKeyMap.REGION,
          filterKeyMap.PLATFORM,
          filterKeyMap.GENRETOR,
          filterKeyMap.HOT_TAGS_SOURCE,
          filterKeyMap.ALL_Games,
          filterKeyMap.ALL_COMPANYS,
        ],
      },
      {
        key: 'game_performance',
        titleKey: 'dbi_filter_group_game_performance',
        filterKeys: RETENTION_D_RANGE_KEYS,
      },
      {
        key: 'sensor_tower',
        titleKey: 'dbi_filter_group_sensor_tower',
        iconCode: 'sensortower',
        filterKeys: SENSOR_TOWER_TAG_KEYS,
      },
      {
        key: 'app_magic',
        titleKey: 'dbi_filter_group_app_magic',
        iconCode: 'appmagic',
        filterKeys: filterKeyMap.APPMAGIC_TAG_KEYS,
      },
      {
        key: 'igdb',
        titleKey: 'dbi_filter_group_igdb',
        iconCode: 'igdb',
        filterKeys: [filterKeyMap.GAME_ENGINE],
      },
    ],
    { ungroupedKeys: [filterKeyMap.MY_FILTER] },
  );

  const filterCfg: TopchartFilterCfgType = {
    ...groupedFilter,
    companyFilterParams: {
      entity_type: 'mobile',
      source: 'sensortower',
      metric: 'retention',
      region_type: 'region',
      regions: ['global'],
      granularity: 'monthly',
      start_date: '2024-01-24',
      end_date: '2024-02-22',
      data_sources: ['appannie', 'sensortower'],
      page_size: 500,
      search_name: '',
      page: 1,
      source_metric: {
        sensortower: ['retention'],
      },
    },
    defaultValue: defaultFilterValue,
    resetFilterValue: {
      ...defaultFilterValue,
      [filterKeyMap.RANGE_TIME]: {
        type: 'month',
        value: [moment().subtract(1, 'months'), moment().subtract(1, 'months')],
      },
    },
    specialParams: {
      myFilterStyle: { width: 140 },
      regionClassName: 'hot-tags-region-select',
      genreClassName: 'hot-tags-genre-select',
      filterTips: getMobileTopChartsFilterTips(formatMessage),
      regionOptions, // 国家筛选器options,
      defalutMarket: ['global'], // 没取到交集用的默认值
      disableNoChina: false,
      rangeTimeType: FilterTimeType.sameMonth,
      // 品类筛选器：四源 + App Magic 四级（4 列），参数格式与 Demographics / Casual TopCharts 一致
      isStdGenreValuesHandler: true,
      genreOverlayClassName: 'cascade-mode',
      genreOptionsLoading: definitionsLoading,
      genreOptions: genreMap,
      genreSources: GENRE_SOURCES_MULTI_DATASOURCE_TOPCHARTS_PAGE,
      disableIegg: false,
      disableAnnie: false,
      disableSensorTower: false,
      sensorTowerTagConfig: tagConfig,
      ...appMagicTagConfig,
      gameEngineOptions: mobileGameEngineOptions,
      globalInfo: formatMessage({ id: `dbi_info_global_filter2` }),
      [filterKeyMap.ALL_Games]: {
        params: { entity_type: entityType, game_type: 'current_game' },
      },
      entityType,
      hotTagsOptions: hotTagsOptions || [],
      filterTagName,
    },
    showConfirmButton: true,
    haveFilterSaveBtn: true,
    handleFilterToApiParams: (params: any) => handleRetentionFilterParamsToApi(params, entityType),
    hasUrlShortLink,
    enableSticky: true,
  };

  return (
    <RetentionComp
      {...props}
      pageId={pageId}
      entityType={entityType}
      pageLogInfo={pageLogInfo}
      filterCfg={filterCfg}
      filterLoading={
        filterLoading
        || tagLoading
        || shortLinkLoading
        || definitionsLoading
        || (!!filterTagName && hotTagsLoading)
      }
      rangeTimeComponentDefault={latestTimeTypeHasData}
      urlCustomParams={urlCustomParams}
      shortLinkValidQuery={shortLinkValidQuery}
      shortLinkFilterValue={shortLinkFilterValue}
      hotTagsOptions={hotTagsOptions}
      filterTagName={filterTagName}
      hotTagsLoading={hotTagsLoading}
      genreOptionsMap={genreOptionsMap}
    />
  );
}

function RetentionComp(props: any) {
  const {
    topChartsType = 'retention',
    // rangeTimeComponentDefault,
    filterLoading,
    filterCfg,
    pageLogInfo,
    shortLinkValidQuery,
    shortLinkFilterValue,
    urlCustomParams,
    entityType,
    hotTagsOptions,
    filterTagName,
    genreOptionsMap,
    pageId,
  } = props;

  const DEFAULT_FORM_VALUE = filterCfg?.defaultValue;

  const [tableLoading, setTableLoading] = useState(true);
  const [isVisualisLoading, setIsVisualisLoading] = useState(true);
  const [sortKeyName, setSortKeyName] = useState<any>(
    shortLinkValidQuery?.[URL_SORT_NAME] || 'mau',
  );
  const [segment, setSegment] = useState(shortLinkValidQuery?.[URL_CHART_METRIC] || 'D2');

  const { formatMessage, locale } = useIntl();

  const { userInfo } = useSelector((state: any) => {
    const { userInfo } = state.home;
    return {
      userInfo,
    };
  });
  // 在筛选器这里拿到缓存的值，并渲染
  const filterSaveName = getFilterName(userInfo);
  const filterSaveData = getFilterValueStorage(userInfo);
  // Retention 使用 sameMonth 时间口径，必须与其他 tab 隔离保存，不能复用全局时间值。
  const { filterExtend, setFilterExtend, isOperate } = useFilterExtend({ pageId });
  const [filterValue, setFilterVale] = useState(isOperate ? filterExtend : DEFAULT_FORM_VALUE);

  // filterRef 用于 HotTagsSource 选项确认时调用 setValueAndConfirm
  const filterRef = React.useRef<any>(null);

  // 当前选中的 hotTag option，用于下载文件名前缀
  const [selectedHotTagOption, setSelectedHotTagOption] = useState<any>(null);

  // hotTagsOptions 加载完成后，匹配 filterTagName 并应用默认值
  useEffect(() => {
    if (!filterTagName || !hotTagsOptions?.length) return;
    const matchedOption = hotTagsOptions.find((opt: any) => opt?.name === filterTagName);
    if (!matchedOption?.value) return;
    setSelectedHotTagOption(matchedOption);
  }, [filterTagName, hotTagsOptions]);

  // 使用 ref 保存最新的 genreOptionsMap，避免 useCallback 闭包陈旧引用
  const genreOptionsMapRef = React.useRef(genreOptionsMap);
  genreOptionsMapRef.current = genreOptionsMap;

  /**
   * App Magic 品类标签点击：回填四列品类筛选器并触发查询
   */
  const handleAppMagicTagClick = React.useCallback(
    (
      _type: AppMagicGenreTagType,
      superTag: string,
      majorTag?: string,
      midTag?: string,
      subTag?: string,
    ) => {
      const appMagicOptions = filterCfg?.specialParams?.genreOptions?.app_magic;
      const genreOptionsLoading = filterCfg?.specialParams?.genreOptionsLoading;
      if (genreOptionsLoading || !appMagicOptions || appMagicOptions.length === 0) {
        return;
      }

      const newGenreValue = generateGenreSelectValueSimple(
        genreOptionsMapRef.current,
        'appmagic',
        superTag,
        majorTag,
        midTag,
        subTag,
      );
      if (!newGenreValue) {
        return;
      }

      filterRef.current?.setValueAndConfirm({
        [filterKeyMap.GENRETOR]: newGenreValue,
      });
    },
    [filterCfg?.specialParams?.genreOptions, filterCfg?.specialParams?.genreOptionsLoading],
  );

  const handleTagClick = React.useCallback(
    buildTopChartsTagClickHandler({
      filterRef,
      filterValue,
      setFilterValue: setFilterVale,
      sensorTowerTagConfig: filterCfg?.specialParams?.sensorTowerTagConfig,
    }),
    [filterCfg?.specialParams?.sensorTowerTagConfig, filterValue],
  );

  // HotTagsSource 选中回调：只更新页面筛选器值，不立即请求
  const handleHotTagConfirm = React.useCallback(async (option: any) => {
    if (!option?.value) return;
    setSelectedHotTagOption(option);
    try {
      const parsedValue = await parseUrlFilterParams(option.value, { genreOptionsMap: genreOptionsMapRef.current });
      if (parsedValue && Object.keys(parsedValue).length > 0) {
        const nextFilterValue = {
          ...DEFAULT_FORM_VALUE,
          ...parsedValue,
          [filterKeyMap.HOT_TAGS_SOURCE]: { value: option },
        };
        setFilterVale(nextFilterValue);
        filterRef.current?.setFormValue(nextFilterValue);
      }
    } catch (err) {
      console.error('[RetentionComp] handleHotTagConfirm failed:', err);
    }
  }, [DEFAULT_FORM_VALUE]);

  // 将 onHotTagConfirm 注入 filterCfg.specialParams
  const filterCfgWithHotTag = React.useMemo(
    () => ({
      ...filterCfg,
      specialParams: {
        ...filterCfg?.specialParams,
        onHotTagConfirm: handleHotTagConfirm,
      },
    }),
    [filterCfg, handleHotTagConfirm],
  );

  const {
    tableData,
    tableTotal,
    lineData,
    fetchParams,
    getGameData,
    getLineChartData,
    tableSelection,
    defaultSelectionGames,
    operatedGame,
    setOperatedGame,
    handleClearSearch,
    transTableDataForSearch,
    isNodata,
    setTableSelection,
    rangeTimeComponentDefault,
  } = useRetentionModel({
    shortLinkValidQuery,
    filterValue,
    setFilterVale,
    hasUrlShortLink: filterCfg?.hasUrlShortLink,
  });

  const { gameDetailsLoading, gameDetailsMapping } = useMobileTopChartsTagDetails(tableData);

  const retentionCompareDateAttr = React.useMemo(() => {
    const { date_type: dateType, date } = fetchParams;
    if (!date) {
      return { dateType };
    }
    const dateM = moment(date);
    if (!dateM.isValid()) {
      return { dateType, startDate: date, endDate: date };
    }
    if (dateType === 'monthly') {
      return {
        dateType,
        startDate: dateM.clone().startOf('month').format('YYYY-MM-DD'),
        endDate: dateM.clone().endOf('month').format('YYYY-MM-DD'),
      };
    }
    if (dateType === 'weekly') {
      return {
        dateType,
        startDate: dateM.clone().startOf('week').format('YYYY-MM-DD'),
        endDate: dateM.clone().endOf('week').format('YYYY-MM-DD'),
      };
    }
    return { dateType, startDate: date, endDate: date };
  }, [fetchParams.date, fetchParams.date_type]);

  useEffect(() => {
    if (shortLinkValidQuery) {
      if (shortLinkValidQuery[URL_CHART_METRIC] !== undefined) {
        setSegment(shortLinkValidQuery[URL_CHART_METRIC]);
      }
      if (shortLinkValidQuery[URL_SORT_NAME] !== undefined) {
        setSortKeyName(shortLinkValidQuery[URL_SORT_NAME]);
      }
    }
  }, [shortLinkValidQuery]);

  const handleConfirm = (value: any, filter, myFilterValue) => {
    const newFormValue = {
      ...stripFetchParamsFilter(fetchParams),
      ...value,
      date: value?.start_date || '',
      page: 1,
      // page_size: 100,
      page_size: 50,
    };
    // 请求接口
    setTableLoading(true);
    setIsVisualisLoading(true);
    setOperatedGame(false);
    getData({
      params: newFormValue,
      isCheckGame: false,
      isSearch: false,
      isUpdateLineChart: true,
      updateFilterValues: filter,
      myFilterValue,
    });
    // getChartData(newFormValue);
  };

  const getData = (args: {
    params: FetchParamsType;
    isCheckGame: boolean;
    isSearch?: boolean;
    isUpdateLineChart: boolean;
    updateFilterValues?: any;
    myFilterValue?: string;
    isInitial?: boolean;
  }) => {
    const {
      params,
      isCheckGame,
      isSearch = false,
      isUpdateLineChart = true,
      updateFilterValues = filterValue,
      myFilterValue = filterSaveName,
      isInitial,
    } = args;
    getGameData({
      params: { ...params, unified_ids: isSearch ? params?.unified_ids : [] },
      isCheckOrSearch: isCheckGame,
      isUpdateLineChart,
      updateFilterValues,
      filterSaveName: myFilterValue || '',
      isInitial,
    })
      .then(() => {
        setTableLoading(false);
        setIsVisualisLoading(false);
        // setIsNewComfirm(true);
      })
      .catch(() => {
        setTableLoading(false);
        setIsVisualisLoading(false);
      });
  };

  const getChartData = (params: FetchParamsType) => {
    if (!params?.unified_ids?.length) {
      return;
    }
    setIsVisualisLoading(true);
    getLineChartData({
      ...params,
      page: 1,
      page_size: 6,
    })
      .then(() => {
        setIsVisualisLoading(false);
      })
      .catch(() => {
        setIsVisualisLoading(false);
      });
  };

  useEffect(() => {
    setTableLoading(true);
    setIsVisualisLoading(true);
    if (!userInfo?.userid || filterLoading) {
      // 拿缓存值需要拿到用户id
      return;
    }
    let newFetchParams = { ...fetchParams };

    const hasUrlShortLinkVal = history.location?.query?.[URL_SHORT_LINK_NAME];
    const { filterValue: newFilterValues, filterParams } = getDbiFilterParams({
      userInfo,
      urlCustomParams,
      urlShortLinkFilter: shortLinkFilterValue,
      filterSaveData: filterSaveData?.apiValue,
      filterExtend,
      specialParams: filterCfg?.specialParams,
      isOperate,
      filterName: filterSaveName,
      filterKeyList: filterCfg?.filterKeyList ?? Object.keys(DEFAULT_FORM_VALUE),
    });

    const newFilterValuesTemp = {
      ...DEFAULT_FORM_VALUE,
      ...newFilterValues,
    };
    // 如果有短链接，设置为继承模式
    if (isOperate || hasUrlShortLinkVal) {
      // 如果目前是继承模式，就设置继承值为过滤后的筛选器值
      setFilterExtend(newFilterValuesTemp);
    }
    setFilterVale(newFilterValuesTemp);
    const apiParams = filterCfg?.handleFilterToApiParams?.(filterParams) || {};
    newFetchParams = {
      ...stripFetchParamsFilter(newFetchParams),
      ...(urlCustomParams || {}),
      ...apiParams,
    };
    getData({
      params: newFetchParams,
      isCheckGame: operatedGame,
      isSearch: false,
      isUpdateLineChart: true,
      updateFilterValues: newFilterValuesTemp,
      isInitial: true,
    });
    // getChartData(newFetchParams);
  }, [userInfo?.userid, filterLoading, shortLinkFilterValue]);

  const onTableSortChage = (e: SortByItemType) => {
    setSortKeyName(e.title);
    const newFetchParams: FetchParamsType = {
      ...fetchParams,
      order: e?.sortVal,
      order_metric: e?.sortMetric || 'mau',
      ratio_type: e?.ratioType,
      page: 1,
      // page_size: 100,
      page_size: 50,
    };
    // setSortKey(e?.sortVal);
    setTableLoading(true);
    // if (!operatedGame) {
    //   /** 没有勾选时点排序则重新Loading柱状图数据 */
    //   setIsVisualisLoading(true);
    //   getChartData(newFetchParams);
    // }
    const retentionChartMetrics = ['D2', 'D3', 'D7', 'D15', 'D31'];
    if (retentionChartMetrics.includes(newFetchParams?.order_metric)) {
      setSegment(newFetchParams.order_metric);
    }
    getData({
      params: newFetchParams,
      isCheckGame: operatedGame,
      isSearch: false,
      isUpdateLineChart: !operatedGame,
    });
  };

  const onTablePagingChange = (e: PagingQuery) => {
    const newFetchParams: FetchParamsType = {
      ...fetchParams,
      page: e?.pageIndex || 1,
      // page_size: e?.pageSize || 100,
      page_size: e?.pageSize || 50,
    };
    setTableLoading(true);
    getData({
      params: newFetchParams,
      isCheckGame: operatedGame,
      isSearch: false,
      isUpdateLineChart: false,
    });
  };

  // 表格列表项选择事件回调
  const onTableCheckGame = (
    selectList: TableSelectionType[],
    checkStatus: TableSelectStatusType,
  ) => {
    // setIsNewComfirm(true);
    // setIsNewCheckGame(true);
    const idList = selectList?.map((item) => item.id);
    const _aipParams = {
      ...fetchParams,
      page: 1,
      page_size: 6,
      unified_ids: [...idList],
    };
    getChartData(_aipParams);
    // /**是否勾选了游戏 */
    if (checkStatus === 'cancel' || checkStatus === 'all' || selectList.length === 0) {
      setOperatedGame(false);
    } else {
      setOperatedGame(true);
    }
    /** 将表格数据转换为柱状或饼 */
    // setBarOrPieData(selectList);
    setTableSelection(selectList);
    /** 处理折线图数据 TODO*/
  };

  // 折线图相关hooks
  const {
    seriesData,
    xAxisData,
    yAxisFormatter,
    tooltipFormatter,
    createLegendsCustomMade,
    currentLine,
  } = useRetentionLineCharts({
    dateType: fetchParams.date_type,
    data: lineData,
    topChartsType,
    segment,
    // currentLineData,
  });

  useEffect(() => {
    if (!filterLoading && filterCfg?.hasUrlShortLink) {
      updateShortLinkPath({
        shortLinkQuery: {
          [URL_SORT_NAME]: fetchParams.order_metric,
          [URL_ORDER]: fetchParams.order,
          [URL_RATIO_TYPE]: fetchParams.ratio_type,
          [URL_CHART_METRIC]: segment,
        },
      });
    }
  }, [filterLoading, segment, fetchParams.order_metric, fetchParams.order, fetchParams.ratio_type]);

  const getTableSearchList = (id: string) => {
    setTableLoading(true);
    getData({
      params: { ...fetchParams, unified_ids: [id] },
      isCheckGame: true,
      isSearch: true,
      isUpdateLineChart: false,
    });
  };

  return (
    <>
      <MarqueeBanner />
      <Card>
        <Card.Body>
          <Filter
            ref={filterRef}
            filterConfirm={handleConfirm}
            filterValue={filterValue}
            setFilterVale={setFilterVale}
            defaultValue={DEFAULT_FORM_VALUE}
            filterCfg={filterCfgWithHotTag}
            extendPageId={pageId}
            rangeTimeComponentDefault={rangeTimeComponentDefault}
            buriedPointParams={pageLogInfo}
          />
        </Card.Body>
      </Card>

      <Card className="dbi-mobileGames-card-context">
        <Card.Body
          title={
            <div style={{ display: 'flex' }}>
              <span style={{ marginRight: 10 }}>{formatMessage({ id: 'dbi_retention' })}</span>
              <ArrowdownText isNew={true} sortedData={sortKeyName} order={fetchParams.order} />
            </div>
          }
        >
          {isVisualisLoading ? (
            <div style={{ height: 402 }}>
              <Loading />
            </div>
          ) : (
            <Visualis
              fetchParams={fetchParams}
              chartType={'line'}
              dateType={fetchParams?.date_type}
              seriesData={seriesData}
              xAxisData={xAxisData}
              yAxisFormatter={yAxisFormatter}
              tooltipFormatter={tooltipFormatter}
              createLegendsCustomMade={createLegendsCustomMade}
              isVisualisLoading={isVisualisLoading}
              currentLine={currentLine}
              setSegment={setSegment}
              sortKeyName={sortKeyName}
              isNodata={isNodata}
              topChartsType={topChartsType}
              titleNow={segment}
              segment={segment}
            />
          )}
        </Card.Body>
      </Card>
      <Justify
        className="dbi-card-justify"
        right={
          <>
            <SearchRecent
              // 传入搜索的filter_ids要为[],不然后端会返回filter_ids里的游戏列表
              fetchParams={{ ...fetchParams, page: 1, filter_ids: [] }}
              handleClear={handleClearSearch}
              getTableResultList={getTableSearchList}
              searchListApi={fetchTopChartsRetentionRankList}
              pageLogInfo={pageLogInfo}
              searchType={'mobile'}
              initSearchInput={!(fetchParams?.unified_ids?.length > 0)}
              transTableDataForSearch={transTableDataForSearch}
            />
            <ExportTable
              entityType="mobile"
              businessType={'mobile-retention'}
              downloadModalSize={820}
              tableParams={{
                ...fetchParams,
                select_ids: tableSelection,
                data_sources: ['sensortower'],
                data_source: 'sensortower',
              }}
              isNeedCheckedLines={true}
              defaultCheckedLines={operatedGame}
              isGameTopCharts={true}
              isDisableDownload={tableLoading}
              total={tableTotal}
              downloadLogConfigKey={'TopChartsMobileGamesRetentionDownloadButton'}
              newStyleFileName={(() => {
                const base = 'Top Charts_Mobile Games_Retention';
                if (!selectedHotTagOption) return base;
                const hotTagName = locale === 'zh-CN'
                  ? (selectedHotTagOption.name_cn || selectedHotTagOption.name || '')
                  : (selectedHotTagOption.name_en || selectedHotTagOption.name || '');
                return hotTagName ? `Hot Tags_${hotTagName}_${base}` : base;
              })()}
            />
          </>
        }
      />
      <CardSlot>
        <RetentionTable
          entityType={entityType}
          records={tableData}
          loading={tableLoading}
          tableTotal={tableTotal}
          pageSortAttr={{
            tablePageIndex: fetchParams.page,
            tablePageSize: fetchParams.page_size,
            currentSortValue: fetchParams.order,
            currentSortMetric: fetchParams.order_metric,
            currentRatioType: fetchParams?.ratio_type,
          }}
          onTableSortChage={onTableSortChage}
          onTablePagingChange={onTablePagingChange}
          onTableCheckGame={onTableCheckGame}
          tableSelection={tableSelection}
          tableSelectionAll={defaultSelectionGames}
          genreSource={fetchParams?.genre_source ?? 'sensortower'}
          compareDateAttr={retentionCompareDateAttr}
          onAppMagicTagClick={handleAppMagicTagClick}
          filterValue={filterValue}
          gameDetailsLoading={gameDetailsLoading}
          gameDetailsMapping={gameDetailsMapping}
          onTagClick={handleTagClick}
          pageId={pageId}
        />
      </CardSlot>
    </>
  );
}
